import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";

import { useDrawdown } from "../api/hooks/useDrawdown";
import { useEquityCurve } from "../api/hooks/useEquityCurve";
import { DrawdownChart } from "../components/charts/DrawdownChart";
import { EquityCurveChart } from "../components/charts/EquityCurveChart";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useGlobalFilters } from "../lib/globalFilters";
import { formatMoney } from "../lib/money";

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

function safeFormatMoney(amount: string, currency: string): string {
  try {
    return formatMoney(amount, { currency });
  } catch {
    return `${amount} ${currency}`;
  }
}

function deriveChangePctFromPoints(
  points: ReadonlyArray<{ cumulative_pnl_amount: string }>,
  startEquity: string,
): string | null {
  if (points.length === 0) return null;
  if (!DECIMAL_RE.test(startEquity)) return null;
  const start = Number(startEquity);
  if (start === 0) return null;
  const lastCum = Number(points[points.length - 1]!.cumulative_pnl_amount);
  return ((lastCum / start) * 100).toFixed(2);
}

function deriveMaxDrawdownPct(
  drawdownAmount: string,
  startEquity: string,
): string | null {
  if (!DECIMAL_RE.test(startEquity) || !DECIMAL_RE.test(drawdownAmount)) {
    return null;
  }
  const start = Number(startEquity);
  if (start === 0) return null;
  const dd = Number(drawdownAmount);
  return ((dd / start) * 100).toFixed(2);
}

interface KpiCardProps {
  title: string;
  value: string;
  hint?: string;
  testId?: string;
}

function KpiCard({ title, value, hint, testId }: KpiCardProps) {
  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-on-surface-variant">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="font-display text-3xl font-medium tracking-tight text-on-surface">{value}</p>
        {hint !== undefined ? (
          <p className="mt-1 text-xs text-on-surface-muted">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function EquityCurvePage() {
  const globalFilters = useGlobalFilters();
  const [includeDeposits, setIncludeDeposits] = useState(false);
  const [includeWithdrawals, setIncludeWithdrawals] = useState(false);
  const [includeDividends, setIncludeDividends] = useState(false);
  const [startEquity, setStartEquity] = useState("");

  const equityCurveQuery = useEquityCurve(globalFilters, {
    include_deposits: includeDeposits,
    include_withdrawals: includeWithdrawals,
    include_dividends: includeDividends,
  });
  const drawdownQuery = useDrawdown(globalFilters);

  const equityPoints = equityCurveQuery.data?.points ?? [];
  const currency = equityCurveQuery.data?.currency ?? "USD";
  const drawdownPoints = drawdownQuery.data?.points ?? [];

  const changePct = useMemo(
    () => deriveChangePctFromPoints(equityPoints, startEquity),
    [equityPoints, startEquity],
  );
  const maxDrawdownPct = useMemo(
    () =>
      drawdownQuery.data === undefined
        ? null
        : deriveMaxDrawdownPct(drawdownQuery.data.max_drawdown_amount, startEquity),
    [drawdownQuery.data, startEquity],
  );

  return (
    <section
      data-testid="equity-curve-page"
      className="space-y-6 p-6"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">Equity Curve</h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Cumulative realized P&amp;L plus optional money flows (deposits,
            withdrawals, dividends).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild>
            <Link to="/accounts" search={{ newTransaction: true }}>
              Add transaction
            </Link>
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-on-surface">
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-on-surface">
              <input
                type="checkbox"
                data-testid="include-deposits-toggle"
                checked={includeDeposits}
                onChange={(e) => setIncludeDeposits(e.target.checked)}
              />
              Include deposits
            </label>
            <label className="flex items-center gap-2 text-sm text-on-surface">
              <input
                type="checkbox"
                data-testid="include-withdrawals-toggle"
                checked={includeWithdrawals}
                onChange={(e) => setIncludeWithdrawals(e.target.checked)}
              />
              Include withdrawals
            </label>
            <label className="flex items-center gap-2 text-sm text-on-surface">
              <input
                type="checkbox"
                data-testid="include-dividends-toggle"
                checked={includeDividends}
                onChange={(e) => setIncludeDividends(e.target.checked)}
              />
              Include dividends
            </label>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="start-equity">Start equity (for Change %)</Label>
              <Input
                id="start-equity"
                data-testid="start-equity-input"
                value={startEquity}
                placeholder="10000.00"
                onChange={(e) => setStartEquity(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div data-testid="equity-curve-kpis" className="grid gap-4 md:grid-cols-2">
        <KpiCard
          title="Change %"
          value={changePct === null ? "—" : `${changePct}%`}
          hint={
            startEquity === ""
              ? "Enter a start equity to compute."
              : `Vs. start equity ${startEquity}`
          }
          testId="kpi-change-pct"
        />
        <KpiCard
          title="Max Drawdown %"
          value={maxDrawdownPct === null ? "—" : `${maxDrawdownPct}%`}
          hint={
            startEquity === ""
              ? "Enter a start equity to compute."
              : `Trough: ${
                  drawdownQuery.data
                    ? safeFormatMoney(
                        drawdownQuery.data.max_drawdown_amount,
                        drawdownQuery.data.currency,
                      )
                    : "—"
                }`
          }
          testId="kpi-max-drawdown-pct"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-on-surface">
            Equity Curve
          </CardTitle>
        </CardHeader>
        <CardContent>
          {equityCurveQuery.isLoading ? (
            <p role="status" className="text-sm text-on-surface-variant">
              Loading equity curve…
            </p>
          ) : equityCurveQuery.isError ? (
            <p role="alert" className="text-sm font-medium text-danger">
              {equityCurveQuery.error instanceof Error
                ? equityCurveQuery.error.message
                : "Failed to load equity curve."}
            </p>
          ) : (
            <EquityCurveChart points={equityPoints} />
          )}
          <p className="mt-2 text-xs text-on-surface-muted">
            Currency: {currency}. Points: {equityPoints.length}.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-on-surface">
            Underwater Drawdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {drawdownQuery.isLoading ? (
            <p role="status" className="text-sm text-on-surface-variant">
              Loading drawdown…
            </p>
          ) : drawdownQuery.isError ? (
            <p role="alert" className="text-sm font-medium text-danger">
              {drawdownQuery.error instanceof Error
                ? drawdownQuery.error.message
                : "Failed to load drawdown."}
            </p>
          ) : (
            <DrawdownChart
              points={drawdownPoints}
              currency={drawdownQuery.data?.currency ?? "USD"}
            />
          )}
          <p className="mt-2 text-xs text-on-surface-muted">
            Distance below the running equity peak (always ≤ 0).
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
